import PropTypes from 'prop-types';
import { useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

// project imports
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import MiniDrawerStyled from './MiniDrawerStyled';

import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import MenuOutlined from '@ant-design/icons/MenuOutlined';

const DRAWER_TOGGLE_NAVY = '#1e3a5f';
const DRAWER_TOGGLE_NAVY_HOVER = '#163152';
const TOGGLE_PX = 32;

// ==============================|| MAIN LAYOUT - DRAWER ||============================== //

export default function MainDrawer({ window }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // responsive drawer container
  const container = window !== undefined ? () => window().document.body : undefined;

  // header content
  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(() => <DrawerHeader open={drawerOpen} />, [drawerOpen]);

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        zIndex: 2000,
        overflowX: 'visible',
        overflowY: 'visible',
        alignSelf: 'stretch',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
      aria-label="mailbox folders"
    >
      {!downLG && (
        <IconButton
          className="sidebar-drawer-toggle"
          onClick={() => handlerDrawerOpen(!drawerOpen)}
          size="small"
          aria-label={drawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          sx={(theme) => ({
            position: 'fixed',
            left: drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
            top: 38,
            transform: 'translateX(-50%)',
            zIndex: theme.zIndex.modal + 1,
            width: TOGGLE_PX,
            height: TOGGLE_PX,
            p: 0,
            borderRadius: '50%',
            bgcolor: DRAWER_TOGGLE_NAVY,
            color: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            transition: theme.transitions.create(['left', 'background-color'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter
            }),
            '&:hover': {
              bgcolor: DRAWER_TOGGLE_NAVY_HOVER,
              color: '#ffffff'
            },
            '& .anticon': { color: '#ffffff' },
            '& .anticon svg': { fill: '#ffffff' }
          })}
        >
          {drawerOpen ? (
            <MenuOutlined style={{ fontSize: 15, color: '#ffffff' }} />
          ) : (
            <CloseOutlined style={{ fontSize: 15, color: '#ffffff' }} />
          )}
        </IconButton>
      )}
      {!downLG ? (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(!drawerOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: drawerOpen ? 'block' : 'none', lg: 'none' } }}
          slotProps={{
            paper: {
              sx: {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                borderRight: '1px solid',
                borderRightColor: '#e0e0e0',
                boxShadow: 'inherit',
                bgcolor: '#ffffff',
                color: 'text.primary',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { width: 0, height: 0, display: 'none' },
                height: '100vh',
                maxHeight: '100dvh',
                minHeight: 0
              }
            }
          }}
        >
          {drawerHeader}
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}

MainDrawer.propTypes = { window: PropTypes.func };
